import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LegalFooterLinks, showLegalLinks } from "./LegalPages";
import pkg from "../../package.json";
const { version } = pkg;

/* ─── SVG Icons ─────────────────────────────────────────────── */

const CopilotIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4l3 3" />
    <path d="M8 12h.01" />
  </svg>
);

const UserIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LightbulbIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M12 2a7 7 0 0 1 7 7c0 2.386-1.156 4.5-2.94 5.83L15 17H9l-1.06-2.17A7 7 0 0 1 12 2z" />
  </svg>
);

const DegreeCapIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c3.33 2 8.67 2 12 0v-5" />
  </svg>
);

const AnalysisIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

/* ─── Data ───────────────────────────────────────────────────── */

const quotes = [
  {
    text: "In today's fast-evolving landscape, support functions face relentless pressure to boost productivity while cutting costs. With 70% of support expenses tied to personnel—and salaries rising even in offshore operations—traditional approaches fall short. Embracing AI-driven automation is no longer optional; it's the key to unlocking unprecedented efficiency and transforming customer success.",
    role: "Chief Customer Officer",
    company: "Fortune 500 Company",
  },
  {
    text: "Customer loyalty fuels our organic growth, but scaling support to deliver exceptional experiences is a formidable challenge. Training teams and managing costs while meeting rising demands requires a bold approach. AI-powered solutions are our catalyst for sustainable, cost-effective scaling that delights customers and drives success.",
    role: "Vice President of Customer Success",
    company: "Fast-Growing Mid-Size Company",
  },
  {
    text: "Our startup's rapid growth demands 24/7 support, but hiring and training engineers fast enough while ensuring quality—especially with offshore resources—has been a steep challenge. AI-powered support empowers our core team of senior engineers to scale instantly, delivering exceptional service from day one.",
    role: "Vice President of Support",
    company: "Fast-Growing Startup Company",
  },
];

const solutions = [
  {
    Icon: CopilotIcon,
    title: "Support Auto Resolution & Co-Pilot",
    bullets: [
      "Understand problem, requests data from customer or gets feed from real-time data sources available",
      "Classifies the problem, and computes the plan of action",
      "Resolves the support request or engages the right-expert to join live",
    ],
  },
  {
    Icon: UserIcon,
    title: "Customer Success Agent",
    bullets: [
      "Sentiment analysis on customer interaction with support Co-pilot and/or live engineer",
      "Proactive notification / escalation of critical cases or customer sat issues to support leaders",
      "Customer Sat Scoring based on the entire interaction with customer",
    ],
  },
  {
    Icon: LightbulbIcon,
    title: "Solution Co-pilot",
    bullets: [
      "Answers customers' questions related to design, configuration, optimization, or security issues",
      "Monitors operational data points that are early indicators of errors within syslogs, release notes, security vulnerabilities",
      "Predicts and proactively notifies solution engineers of potential problems",
    ],
  },
  {
    Icon: DegreeCapIcon,
    title: "Instant Learning",
    bullets: [
      "Automatically learns from every update to the data sources and every customer interaction",
      "As a copilot continuously guides the support engineer with available debugging options",
    ],
  },
  {
    Icon: AnalysisIcon,
    title: "Analytics & Insights",
    bullets: [
      "Provides analytics on case resolution metrics by case type, assigned engineers, etc.",
    ],
  },
  {
    Icon: SettingsIcon,
    title: "Seamless Integrations",
    bullets: [
      "Easy integration with all the data sources that trains our diagnostics engine",
      "Easily integrates with customers Web portal for consistent branding or preferred tools",
    ],
  },
];

const valueProps = [
  {
    title: "Support Cost Reduction",
    desc: "Arta's state-of-the-art Diagnostics engine auto-resolves up to 50% of customer support cases and boosts engineer productivity by 50% when escalation is needed, driving unparalleled efficiency and customer satisfaction.",
  },
  {
    title: "Customer Success",
    desc: "Arta's Support Diagnostics engine raises the floor of customer experience with proven AI-driven diagnostics, pinpointing issues to slash time-to-resolution and deliver consistent, reliable support at scale.",
  },
  {
    title: "Premium Services Revenue",
    desc: "Arta's real-time monitoring of application behavior and its revolutionary AI engine enables enterprise vendors to offer premium add-on services.",
  },
];

const founders = [
  {
    name: "Burjiz Pithawala",
    role: "Co-Founder & CEO",
    bio: "Burjiz Pithawala leads Arta's Product Strategy. He pioneered the development of one of the tech industry's first Support Automation Platforms at Cisco Systems—a groundbreaking innovation that not only greatly accelerated time-to-resolution for customer support issues but also laid the foundation for delivering proactive services to customers. Burjiz has held multiple senior executive and General Manager roles at leading companies including Cisco and Arrcus, and he was the founder and CEO of Elisity, a successful security platform company. He holds a Master's degree in Computer Science.",
  },
  {
    name: "Brad Kashani",
    role: "Co-Founder & CBO",
    bio: "Brad Kashani leads Arta's Customer and Business Operations. With over 20 years of executive leadership, Brad has built world-class support and consulting organizations recognized with numerous industry awards at companies such as Cisco and Juniper Networks. He has been a trailblazer in the services sector, pioneering Cisco's Advanced Services—a multi-billion dollar business today—and spearheading the development of Support Automation platforms that redefined customer experience at Cisco, Procket, and Juniper Networks. Brad holds a Master's degree in Computer Science from the University of Colorado and an MBA from UCLA.",
  },
];

/* ─── Avatar placeholder ─────────────────────────────────────── */
const AvatarPlaceholder = ({ name }) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("");
  return (
    <div className="w-28 h-28 rounded-full bg-gradient-to-br from-cyan-400 to-teal-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg select-none">
      {initials}
    </div>
  );
};

/* ─── Component ──────────────────────────────────────────────── */
const AboutUs = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#0d1f26";
    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0d1f26] text-white font-sans">

      {/* ── Nav Bar ── */}
      <nav className="sticky top-0 z-50 bg-[#0d1f26]/95 backdrop-blur border-b border-white/10 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="/A_bgless.png"
            alt="Arta Logo"
            className="h-9 w-auto"
            onError={(e) => { e.target.onerror = null; e.target.src = "/A.png"; }}
          />
          <span className="text-white font-semibold text-lg tracking-wide">Arta</span>
          <span className="bg-white/10 text-white/50 text-xs px-2 py-0.5 rounded-full border border-white/10">
            v{version}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="mailto:email@artasupport.ai"
            className="text-sm text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition"
          >
            Click here for Demo
          </a>
          <button
            onClick={() => navigate(-1)}
            className="bg-cyan-500 hover:bg-cyan-600 text-white text-sm px-4 py-1.5 rounded-lg shadow transition"
          >
            ← Back
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative py-24 px-6 text-center overflow-hidden">
        {/* subtle radial glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-cyan-500/10 blur-[120px] rounded-full" />
        </div>
        <div className="relative max-w-4xl mx-auto flex flex-col items-center gap-6">
          <img
            src="/A_bgless.png"
            alt="Arta Logo"
            className="h-24 w-auto drop-shadow-lg"
            onError={(e) => { e.target.onerror = null; e.target.src = "/A.png"; }}
          />
          <h1 className="text-4xl md:text-5xl font-bold leading-tight text-white">
            Enabling Enterprises to Improve<br />
            <span className="text-cyan-400">Customer Loyalty and Support Cost</span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-3xl leading-relaxed">
            Transform technical support with AI Pilots and co-pilots delivering consistently
            exceptional customer experiences and empowering support teams to achieve{" "}
            <span className="text-cyan-300 font-semibold">50% greater productivity</span>.
          </p>
          <a
            href="mailto:email@artasupport.ai"
            className="mt-2 inline-block bg-cyan-500 hover:bg-cyan-400 text-white font-semibold px-8 py-3 rounded-xl shadow-lg transition text-base"
          >
            Request a Demo →
          </a>
        </div>
      </section>

      {/* ── What Support Leaders Are Saying ── */}
      <section className="py-20 px-6 bg-[#0a191f]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-white mb-12">
            What Support Leaders Are Saying
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {quotes.map((q, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-2xl p-7 flex flex-col gap-5 hover:border-cyan-500/40 transition"
              >
                {/* quote mark */}
                <span className="text-cyan-400 text-5xl leading-none font-serif select-none">"</span>
                <p className="text-white/75 text-sm leading-relaxed flex-1">{q.text}</p>
                <div className="border-t border-white/10 pt-4">
                  <p className="text-cyan-300 font-semibold text-sm">{q.role}</p>
                  <p className="text-white/50 text-xs mt-0.5">{q.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Arta's Comprehensive Solutions ── */}
      <section className="py-20 px-6 bg-[#0d1f26]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-white mb-2 tracking-wide uppercase">
            Arta's Comprehensive Solutions
          </h2>
          <p className="text-center text-white/40 text-sm mb-12 uppercase tracking-widest">
            AI-Powered. Enterprise-Ready.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map(({ Icon, title, bullets }, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-4 hover:border-cyan-500/40 hover:bg-white/8 transition"
              >
                <div className="text-cyan-400">
                  <Icon />
                </div>
                <h3 className="text-white font-semibold text-base leading-snug">{title}</h3>
                <ul className="flex flex-col gap-2">
                  {bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-white/65 text-sm leading-relaxed">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Value Proposition ── */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0a191f] to-[#0d1f26]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-white mb-12">
            Arta's Value Proposition
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {valueProps.map(({ title, desc }, i) => (
              <div
                key={i}
                className="relative bg-gradient-to-br from-cyan-500/10 to-teal-500/5 border border-cyan-500/20 rounded-2xl p-7 flex flex-col gap-3 hover:border-cyan-400/40 transition"
              >
                <div className="w-8 h-1 bg-cyan-400 rounded-full mb-1" />
                <h3 className="text-cyan-300 font-bold text-lg">{title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Meet Our Founders ── */}
      <section className="py-20 px-6 bg-[#0a191f]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-white mb-12">
            Meet Our Founders
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {founders.map(({ name, role, bio }, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 flex flex-col items-center text-center gap-5 hover:border-cyan-500/40 transition"
              >
                <AvatarPlaceholder name={name} />
                <div>
                  <h3 className="text-white font-bold text-xl">{name}</h3>
                  <p className="text-cyan-400 text-sm font-medium mt-1">{role}</p>
                </div>
                <p className="text-white/65 text-sm leading-relaxed text-left">{bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-10 px-6 bg-[#0d1f26] border-t border-white/10 text-center">
        <img
          src="/A_bgless.png"
          alt="Arta Logo"
          className="h-8 w-auto mx-auto mb-3 opacity-60"
          onError={(e) => { e.target.onerror = null; e.target.src = "/A.png"; }}
        />
        <p className="text-white/40 text-sm">
          © {new Date().getFullYear()} Arta Inc. · San Jose, CA ·{" "}
          <a href="mailto:email@artasupport.ai" className="text-cyan-500 hover:text-cyan-400 transition">
            email@artasupport.ai
          </a>
        </p>
        {showLegalLinks && (
          <p className="text-white/40 text-sm mt-2">
            <LegalFooterLinks />
          </p>
        )}
        <p className="text-white/20 text-xs mt-1">v{version}</p>
      </footer>

    </div>
  );
};

export default AboutUs;
